import { CSSProperties, useEffect, useMemo, useRef } from "react";
import { NetworkEvent, Topology, ViewerState } from "./protocol";
import {
  currentGoalNetworkDynamics, currentGoalNetworkInputValues, currentGoalNetworkReadouts,
  currentGoalNetworkReadoutTicks, currentGoalVehicleInputs, goalLearningStatusLabel,
  selectGoalNetworkPulses,
} from "./goal-network-events";
import "./goal-network.css";

interface GoalNetworkViewProps {
  state: ViewerState;
  events: NetworkEvent[];
  topology: Topology | null;
  error: string;
}

interface Point { x: number; y: number }

const INPUTS = [
  [105, "LOOKAHEAD κ(20 m)"], [165, "LOOKAHEAD κ(45 m)"], [225, "LATERAL e⊥"],
  [285, "SPEED v"], [345, "PHASE φ"], [405, "pit_requested"], [465, "START INDEX"],
] as const;

const DRIVER_GROUPS = [
  { y: 120, label: "EFFECTIVE STEERING", readout: "driver.steering", fallback: "—" },
  { y: 235, label: "THROTTLE", readout: "driver.throttle", fallback: "—" },
  { y: 350, label: "BRAKE PRESSURE", readout: "driver.brake", fallback: "—" },
  { y: 465, label: "GEAR", readout: "driver.gear", fallback: "—" },
] as const;

const VEHICLE_GROUPS = [
  { y: 155, count: 12, rows: 3, label: "TURN RESPONSE", input: "TURN CORE INPUT", detail: "effective steering + speed", readout: "vehicle.turn" },
  { y: 315, count: 7, rows: 2, label: "DRIVE RESPONSE", input: "DRIVE CORE INPUT", detail: "throttle + gear", readout: "vehicle.drive" },
  { y: 475, count: 12, rows: 3, label: "BRAKE RESPONSE", input: "BRAKE CORE INPUT", detail: "front brake pressure + speed", readout: "vehicle.brake" },
] as const;

const PHYSICAL_PATHS = [
  { id: "physical.driver-steering-turn", d: "M620 120 C675 120 700 155 755 155" },
  { id: "physical.driver-throttle-drive", d: "M620 235 C675 235 700 315 755 315" },
  { id: "physical.driver-brake-brake", d: "M620 350 C675 350 700 475 755 475" },
  { id: "physical.driver-gear-drive", d: "M620 465 C675 465 700 315 755 315" },
  { id: "physical.speed-turn", d: "M740 555 C795 555 700 155 755 155" },
  { id: "physical.speed-brake", d: "M740 555 C795 555 700 475 755 475" },
  { id: "physical.vehicle-turn-state", d: "M1325 155 C1380 155 1335 315 1390 315" },
  { id: "physical.vehicle-drive-state", d: "M1325 315 C1380 315 1335 315 1390 315" },
  { id: "physical.vehicle-brake-state", d: "M1325 475 C1380 475 1335 315 1390 315" },
] as const;
const DRIVER_PHYSICAL_PATH_IDS = PHYSICAL_PATHS.slice(0, 4).map(path => path.id);

const driverBranchPoints = (centerY: number): Point[] => Array.from({ length: 6 }, (_, index) => ({
  x: 325 + (index % 3) * 35,
  y: centerY - 18 + Math.floor(index / 3) * 36,
}));

const vehicleBranchPoints = (centerY: number, count: number, rows: number): Point[] =>
  Array.from({ length: count }, (_, index) => {
    const row = Math.floor(index / 4);
    const col = index % 4;
    const rowWidth = Math.min(4, count - row * 4);
    return { x: 1015 + col * 30 + (4 - rowWidth) * 15, y: centerY + (row - (rows - 1) / 2) * 28 };
  });

const formatReadout = (readout: NetworkEvent["readout"], fallback = "—", tick?: string): string =>
  readout ? `${compactNumbers(readout.value)}${readout.unit ? ` ${readout.unit}` : ""}${tick ? ` · T${tick}` : ""}` : fallback;

const compactNumbers = (value: string): string => value.replace(
  /[-+]?(?:\d+\.\d+|\.\d+)(?:[eE][-+]?\d+)?/g,
  token => {
    const number = Number(token);
    if (!Number.isFinite(number)) return token;
    const magnitude = Math.abs(number);
    if (magnitude !== 0 && (magnitude < 0.0001 || magnitude >= 10000)) return number.toExponential(3);
    return number.toFixed(4).replace(/\.?0+$/, "");
  },
);

const constrainedTextLength = (text: string, maximum: number): number | undefined =>
  text.length > 18 ? maximum : undefined;

const displayUnit = (unit: string): string => unit
  .replace(/^telemetry_/, "")
  .replace(/_units$/, " units")
  .replaceAll("_", " ");

const PULSE_DURATION_MS = 775;
const PULSE_CANVAS_WIDTH = 1500;
const PULSE_CANVAS_HEIGHT = 620;
const PULSE_PATH_SAMPLE_COUNT = 48;

interface PulsePoint { x: number; y: number }
interface CanvasPulse {
  pathId: string;
  direction: string;
  startMs: number;
}

const pulseDelayMs = (event: NetworkEvent): number => {
  const pathId = event.display_path_id ?? "";
  const stageDelay = pathId.startsWith("input.") ? 0
    : pathId.startsWith("encoder.") ? 90
      : pathId.startsWith("driver.") ? 190
        : pathId.startsWith("physical.driver-") || pathId.startsWith("physical.speed-") ? 290
          : pathId.includes(".input.branch.") ? 390
            : pathId.startsWith("vehicle.") ? 490
              : 590;
  const laneDelay = [...event.event_id].reduce((sum, character) => sum + character.charCodeAt(0), 0) % 90;
  return stageDelay + laneDelay;
};

const PULSE_PALETTES = {
  physical: { glow: "rgba(231, 25, 63, .22)", body: "#ff4a24", core: "#fff8dd" },
  packet: { glow: "rgba(240, 68, 32, .2)", body: "#ff9f2b", core: "#fffbe8" },
} as const;

interface PulseBatch { opacity: number; physical: boolean; points: PulsePoint[] }

function PulseCanvas({ events, state }: { events: NetworkEvent[]; state: ViewerState }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pulses = useRef<CanvasPulse[]>([]);
  const geometry = useRef(new Map<string, PulsePoint[]>());
  const canvasScale = useRef({ x: 1, y: 1 });
  const previousTime = useRef(state.raceTimeMs);
  const identity = `${state.carId}|${state.seekGeneration}`;
  const previousIdentity = useRef(identity);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (canvas && context) {
      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.restore();
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(bounds.width * ratio));
      canvas.height = Math.max(1, Math.round(bounds.height * ratio));
      canvasScale.current = {
        x: canvas.width / PULSE_CANVAS_WIDTH,
        y: canvas.height / PULSE_CANVAS_HEIGHT,
      };
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const identityChanged = previousIdentity.current !== identity;
    if (identityChanged || state.playState !== "playing" || state.raceTimeMs < previousTime.current) {
      previousIdentity.current = identity;
      previousTime.current = state.raceTimeMs;
      pulses.current = [];
      geometry.current.clear();
      clearCanvas();
      return;
    }
    const selected = selectGoalNetworkPulses(events, {
      carId: state.carId,
      seekGeneration: state.seekGeneration,
      playState: state.playState,
      previousRaceTimeMs: previousTime.current,
      raceTimeMs: state.raceTimeMs,
    });
    previousTime.current = state.raceTimeMs;
    const now = performance.now();
    for (const event of selected) {
      const pathId = event.display_path_id;
      if (!pathId) continue;
      pulses.current.push({
        pathId,
        direction: event.direction,
        startMs: now + pulseDelayMs(event),
      });
    }
  }, [events, identity, state.carId, state.playState, state.raceTimeMs, state.seekGeneration]);

  useEffect(() => {
    if (state.playState !== "playing") return;
    let frame = 0;
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    const svg = canvas.parentElement?.querySelector<SVGSVGElement>("svg.goal-network-base");

    const sampledPath = (pathId: string): PulsePoint[] | null => {
      const cached = geometry.current.get(pathId);
      if (cached) return cached;
      const edge = svg?.querySelector<SVGGeometryElement>(`[data-edge-id="${CSS.escape(pathId)}"]`);
      if (!edge) return null;
      const length = edge.getTotalLength();
      const points = Array.from({ length: PULSE_PATH_SAMPLE_COUNT + 1 }, (_, index) => {
        const point = edge.getPointAtLength(length * index / PULSE_PATH_SAMPLE_COUNT);
        return { x: point.x, y: point.y };
      });
      geometry.current.set(pathId, points);
      return points;
    };

    const draw = (now: number) => {
      context.setTransform(canvasScale.current.x, 0, 0, canvasScale.current.y, 0, 0);
      context.clearRect(0, 0, PULSE_CANVAS_WIDTH, PULSE_CANVAS_HEIGHT);
      const active: CanvasPulse[] = [];
      const batches = new Map<string, PulseBatch>();
      for (const pulse of pulses.current) {
        const progress = (now - pulse.startMs) / PULSE_DURATION_MS;
        if (progress < 0) {
          active.push(pulse);
          continue;
        }
        if (progress >= 1) continue;
        const points = sampledPath(pulse.pathId);
        if (!points) continue;
        const pointIndex = progress * PULSE_PATH_SAMPLE_COUNT;
        const lower = Math.floor(pointIndex);
        const upper = Math.min(PULSE_PATH_SAMPLE_COUNT, lower + 1);
        const fraction = pointIndex - lower;
        const x = points[lower].x + (points[upper].x - points[lower].x) * fraction;
        const y = points[lower].y + (points[upper].y - points[lower].y) * fraction;
        const fade = progress < .06 ? progress / .06 : progress > .88 ? (1 - progress) / .12 : 1;
        const opacity = Math.max(.25, Math.ceil(fade * 4) / 4);
        const physical = pulse.direction === "physical_signal";
        const batchKey = `${physical ? "physical" : "packet"}:${opacity}`;
        const batch = batches.get(batchKey) ?? { opacity, physical, points: [] };
        batch.points.push({ x, y });
        batches.set(batchKey, batch);
        active.push(pulse);
      }
      pulses.current = active;
      for (const batch of batches.values()) {
        const palette = batch.physical ? PULSE_PALETTES.physical : PULSE_PALETTES.packet;
        context.globalAlpha = batch.opacity;
        context.beginPath();
        for (const point of batch.points) {
          context.moveTo(point.x + 5.5, point.y);
          context.arc(point.x, point.y, 5.5, 0, Math.PI * 2);
        }
        context.fillStyle = palette.glow;
        context.fill();
        context.beginPath();
        for (const point of batch.points) {
          context.moveTo(point.x + 2.8, point.y);
          context.arc(point.x, point.y, 2.8, 0, Math.PI * 2);
        }
        context.fillStyle = palette.body;
        context.fill();
        context.beginPath();
        for (const point of batch.points) {
          context.moveTo(point.x + 1.1, point.y);
          context.arc(point.x, point.y, 1.1, 0, Math.PI * 2);
        }
        context.fillStyle = palette.core;
        context.fill();
      }
      context.globalAlpha = 1;
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.restore();
    };
  }, [identity, state.playState]);

  return <canvas
    ref={canvasRef} className="goal-network-pulse-canvas" data-layer="pulses"
    data-testid="network-pulse-canvas" aria-hidden="true"
  />;
}

export function GoalNetworkView({ state, events, topology, error }: GoalNetworkViewProps) {
  const readouts = useMemo(() => currentGoalNetworkReadouts(
    events, state.carId, state.seekGeneration, state.raceTimeMs,
  ), [events, state.carId, state.raceTimeMs, state.seekGeneration]);
  const readoutTicks = useMemo(() => currentGoalNetworkReadoutTicks(
    events, state.carId, state.seekGeneration, state.raceTimeMs,
  ), [events, state.carId, state.raceTimeMs, state.seekGeneration]);
  const inputValues = useMemo(() => currentGoalNetworkInputValues(
    events, state.carId, state.seekGeneration, state.raceTimeMs,
  ), [events, state.carId, state.raceTimeMs, state.seekGeneration]);
  const vehicleInputs = useMemo(() => currentGoalVehicleInputs(
    events, state.carId, state.seekGeneration, state.raceTimeMs,
  ), [events, state.carId, state.raceTimeMs, state.seekGeneration]);
  const dynamics = useMemo(() => currentGoalNetworkDynamics(events, {
    carId: state.carId, seekGeneration: state.seekGeneration,
    playState: state.playState, raceTimeMs: state.raceTimeMs,
  }), [events, state.carId, state.playState, state.raceTimeMs, state.seekGeneration]);
  const dynamicStyle = (pathId: string, baseWidth: number): CSSProperties | undefined => {
    const value = dynamics.get(pathId);
    if (!value) return undefined;
    return {
      opacity: 0.35 + value.intensity * 0.65,
      strokeWidth: baseWidth + value.intensity * 2.4,
      filter: `drop-shadow(0 0 ${2 + value.intensity * 6}px currentColor)`,
    };
  };
  const branchStyle = (pathId: string): CSSProperties | undefined => {
    const value = dynamics.get(pathId);
    return value ? { strokeWidth: 1.5 + value.intensity * 2, filter: `drop-shadow(0 0 ${2 + value.intensity * 7}px currentColor)` } : undefined;
  };
  const currentText = (pathId: string): string => {
    const value = inputValues.get(pathId);
    return value ? compactNumbers(value.value) : "—";
  };
  const currentUnit = (pathId: string): string => displayUnit(inputValues.get(pathId)?.unit ?? "");
  const vehicleCurrentLines = (paths: Array<[string, string]>): string[] => {
    const values = paths.map(([path, label]) => ({ label, value: vehicleInputs.get(path) })).filter(item => item.value !== undefined);
    if (!values.length) return ["—"];
    const tick = values[0].value!.tick.split("/").at(-1);
    const lines = values.flatMap(({ label, value }) => {
      const [first, ...rest] = value!.value.split(" / ");
      if (!rest.length) return [`${label} ${first}${value!.unit ? ` ${displayUnit(value!.unit)}` : ""}`];
      return [`${label} ${first}`, `${rest.join(" / ")}${value!.unit ? ` ${displayUnit(value!.unit)}` : ""}`];
    }).map(compactNumbers);
    lines[lines.length - 1] += ` · TICK ${tick}`;
    return lines;
  };
  const learningLabel = goalLearningStatusLabel(topology?.learning_status);

  return (
    <section className="goal-network-panel" data-topology-hash={topology?.topology_hash ?? ""}>
      <div className="goal-network-heading">
        <span>AZPNET / LEARNED-STATE REPLAY</span>
        <span>{learningLabel} · {state.playState === "playing" ? "LIVE EVENT FLOW" : "EVENT FLOW PAUSED"}</span>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="goal-network-scroll">
        <div className="goal-network-svg-stack">
        <svg className="goal-network-base" data-layer="links" viewBox="0 0 1500 620" role="img" aria-labelledby="goal-network-title goal-network-desc">
          <title id="goal-network-title">AzpNet driver and vehicle signal network</title>
          <desc id="goal-network-desc">Seven situation inputs feed four driver neurons, cross a physical boundary, feed three vehicle neurons, and converge on vehicle state. Real events emit one finite pulse.</desc>
          <rect x="0" y="68" width="650" height="440" rx="18" className="gn-lane" />
          <rect x="730" y="68" width="770" height="440" rx="18" className="gn-lane" />
          <line x1="690" y1="68" x2="690" y2="535" className="gn-divider" />

          {[[72,"SITUATION INPUTS"],[200,"DATA LINK"],[360,"M_g LAYER"],[585,"DRIVER f OUTPUTS"],[825,"VEHICLE INPUT"],[1060,"M_g LAYER"],[1290,"VEHICLE OUTPUT"],[1440,"STATE"]].map(([x,label]) =>
            <text key={label} x={x} y="30" className="gn-heading" textAnchor="middle">{label}</text>)}
          <text x="200" y="52" className="gn-secondary" textAnchor="middle">AR(16) + embedding</text>
          <text x="360" y="52" className="gn-secondary" textAnchor="middle">4 neurons × 6 Möbius branches</text>
          <text x="585" y="52" className="gn-secondary" textAnchor="middle">Σ + bias → f</text>
          <text x="1060" y="52" className="gn-secondary" textAnchor="middle">12 + 7 + 12 Möbius activation branches</text>
          <text x="1290" y="52" className="gn-secondary" textAnchor="middle">Σ + bias → f</text>
          <text x="690" y="558" className="gn-secondary" textAnchor="middle">PHYSICAL BOUNDARY</text>

          <g>
            {INPUTS.map(([y, label], inputIndex) => <g key={label}>
              <line x1="135" y1={y} x2="182" y2={y} className="gn-edge gn-driver-flow" data-edge-id={`input.${inputIndex}.encoder`} style={dynamicStyle(`input.${inputIndex}.encoder`, .75)} />
              {DRIVER_GROUPS.flatMap((group, groupIndex) => driverBranchPoints(group.y).map((point, branchIndex) =>
                <line key={`${label}-${group.label}-${branchIndex}`} x1="218" y1={y} x2={point.x - 11} y2={point.y} className="gn-edge gn-input-flow" data-edge-id={`encoder.${inputIndex}.driver.${groupIndex}.branch.${branchIndex}`} style={dynamicStyle(`encoder.${inputIndex}.driver.${groupIndex}.branch.${branchIndex}`, .75)} />))}
            </g>)}
            {DRIVER_GROUPS.map((group, groupIndex) => <g key={group.label}>
              {driverBranchPoints(group.y).map((point, branchIndex) =>
                <line key={branchIndex} x1={point.x + 11} y1={point.y} x2="550" y2={group.y} className="gn-edge gn-driver-flow" data-edge-id={`driver.${groupIndex}.branch.${branchIndex}.output`} style={dynamicStyle(`driver.${groupIndex}.branch.${branchIndex}.output`, .75)} />)}
            </g>)}
            {PHYSICAL_PATHS.map(path => <path key={path.id} d={path.d} className="gn-physical" data-edge-id={path.id} style={dynamicStyle(path.id, 1.35)} />)}
            {VEHICLE_GROUPS.map((group, groupIndex) => <g key={group.label}>
              {vehicleBranchPoints(group.y, group.count, group.rows).map((point, branchIndex) => <g key={branchIndex}>
                <line x1="895" y1={group.y} x2={point.x - 9} y2={point.y} className="gn-edge gn-vehicle-flow" data-edge-id={`vehicle.${groupIndex}.input.branch.${branchIndex}`} style={dynamicStyle(`vehicle.${groupIndex}.input.branch.${branchIndex}`, .75)} />
                <line x1={point.x + 9} y1={point.y} x2="1255" y2={group.y} className="gn-edge gn-vehicle-flow" data-edge-id={`vehicle.${groupIndex}.branch.${branchIndex}.output`} style={dynamicStyle(`vehicle.${groupIndex}.branch.${branchIndex}.output`, .75)} />
              </g>)}
            </g>)}
          </g>

          <text x="750" y="592" className="gn-secondary" textAnchor="middle">All forward links run left → right. The grids are display-only wrapping of parallel branches.</text>
        </svg>

          <PulseCanvas events={events} state={state} />

        <svg className="goal-network-node-layer" data-layer="nodes" viewBox="0 0 1500 620" aria-hidden="true">
          <g>
            {INPUTS.map(([y, label], inputIndex) => <g key={label}>
              <text x="5" y={y + 5} className="gn-label">{label}</text>
              <text x="5" y={y + 18} className="gn-current-value">{currentText(`input.${inputIndex}.encoder`)}</text>
              {currentUnit(`input.${inputIndex}.encoder`) && <text
                x="5" y={y + 27} className="gn-current-unit"
                textLength={constrainedTextLength(currentUnit(`input.${inputIndex}.encoder`), 112)}
                lengthAdjust="spacingAndGlyphs"
              >{currentUnit(`input.${inputIndex}.encoder`)}</text>}
              <circle cx="125" cy={y} r="10" className="gn-node" data-node-id={`input.${inputIndex}`} />
              <circle cx="200" cy={y} r="18" className="gn-node gn-encoder" data-node-id={`encoder.${inputIndex}`} />
              <text x="200" y={y + 5} className="gn-secondary" textAnchor="middle">ENC</text>
            </g>)}
            {DRIVER_GROUPS.map((group, groupIndex) => <g key={group.label}>
              {driverBranchPoints(group.y).map((point, branchIndex) =>
                <circle key={branchIndex} cx={point.x} cy={point.y} r="11" className="gn-driver-branch" data-node-id={`driver.${groupIndex}.branch.${branchIndex}`} style={branchStyle(`driver.${groupIndex}.branch.${branchIndex}.output`)} />)}
              <circle cx="585" cy={group.y} r="35" className="gn-output-node" data-node-id={`driver.${groupIndex}`} />
              <text x="585" y={group.y + 6} className="gn-heading" textAnchor="middle">f</text>
              <text x="585" y={group.y + 58} className="gn-secondary" textAnchor="middle">{group.label}</text>
              <text x="585" y={group.y + 78} className="gn-readout" textAnchor="middle" data-driver-output={group.label}>{formatReadout(readouts.get(group.readout), group.fallback, readoutTicks.get(group.readout))}</text>
            </g>)}
            {VEHICLE_GROUPS.map((group, groupIndex) => {
            const currentLines = vehicleCurrentLines([
              [DRIVER_PHYSICAL_PATH_IDS[groupIndex], groupIndex === 0 ? "STEER" : groupIndex === 1 ? "THROTTLE" : "BRAKE"],
              [groupIndex === 0 ? "physical.speed-turn" : groupIndex === 1 ? "physical.driver-gear-drive" : "physical.speed-brake", groupIndex === 1 ? "GEAR" : "SPEED"],
            ]);
            const rowSpacing = 11;
            // SVG text metrics reserve more descent than the visible glyphs use.
            // Shift the centered baseline group down two pixels for optical balance.
            const firstRowY = group.y - ((currentLines.length + 1) * rowSpacing) / 2 + 2;
              return <g key={group.label}>
                <rect x="755" y={group.y - 34} width="140" height="68" rx="10" className="gn-core-input" />
                <text x="825" y={firstRowY} className="gn-heading" textAnchor="middle" dominantBaseline="middle">{group.input}</text>
                <text x="825" y={firstRowY + rowSpacing} className="gn-secondary" textAnchor="middle" dominantBaseline="middle">{group.detail}</text>
                {currentLines.map((line, lineIndex) => <text
                  key={lineIndex} x="825" y={firstRowY + (lineIndex + 2) * rowSpacing} className="gn-current-value" textAnchor="middle"
                  dominantBaseline="middle" textLength={constrainedTextLength(line, 130)} lengthAdjust="spacingAndGlyphs"
                >{line}</text>)}
                {vehicleBranchPoints(group.y, group.count, group.rows).map((point, branchIndex) =>
                  <circle key={branchIndex} cx={point.x} cy={point.y} r="9" className="gn-vehicle-branch" data-node-id={`vehicle.${groupIndex}.branch.${branchIndex}`} style={branchStyle(`vehicle.${groupIndex}.branch.${branchIndex}.output`)} />)}
                <circle cx="1290" cy={group.y} r="35" className="gn-output-node" data-node-id={`vehicle.${groupIndex}`} />
                <text x="1290" y={group.y + 6} className="gn-heading" textAnchor="middle">f</text>
                <text x="1290" y={group.y + 58} className="gn-secondary" textAnchor="middle">{group.label}</text>
                <text x="1290" y={group.y + 78} className="gn-readout" textAnchor="middle" data-vehicle-output={group.label}>{formatReadout(readouts.get(group.readout), "—", readoutTicks.get(group.readout))}</text>
              </g>})}
            <circle cx="715" cy="555" r="25" className="gn-node" data-node-id="vehicle.speed-state" />
            <text x="715" y="552" className="gn-heading" textAnchor="middle">SPEED</text>
            <text x="715" y="571" className="gn-secondary" textAnchor="middle">vehicle state</text>
            <rect x="1390" y="280" width="100" height="70" rx="10" className="gn-core-input" data-node-id="vehicle.state" />
            <text x="1440" y="310" className="gn-heading" textAnchor="middle">VEHICLE</text>
            <text x="1440" y="333" className="gn-heading" textAnchor="middle">STATE</text>
          </g>

        </svg>
        </div>
      </div>
      <p className="goal-network-boundary-note">Signal light is emitted only by a runtime event carrying the current car, seek generation, and an approved display path.</p>
    </section>
  );
}
